import { useMemo, useState } from 'react';
import { UseFormReset } from 'react-hook-form';

import { DEFAULT_COUNTRY, DEFAULT_REQUIRED_FIELDS } from 'addresses/constants';
import { AddressFields } from 'addresses/enums';
import { DEFAULT_BILL_TO_ADDRESS_ACCOUNTS_RECEIVABLE_ID } from 'contacts/constants';
import { IContactGroup, IContactGroupAddress } from 'contacts/types';
import { FormProvider, useNotificationContext } from 'core/states';
import { ICorporateUserAutocomplete } from 'corporate/types';

import { EmployeeWarning } from './EmployeeWarning';
import { ContactGroupAddressType } from './enums';
import { mapUpdateContactGroupAddressForm } from './mappers';
import { updateQuoteAddressSchema } from './schema';
import { IQuoteAddressForm, IQuoteAddressType } from './types';
import { UpdateAddressByTypeFormFields } from './UpdateAddressByTypeFormFields';
import { getAccountsReceivableId, getDefaultValues } from './utils';

interface IProps<T> {
  isOpen: boolean;
  parent: T;
  onSubmit: (mappedData: IQuoteAddressType) => Promise<{
    success: boolean;
    updatedParent: T;
  }>;
  onClose: (updatedParent?: T) => void;
  type: ContactGroupAddressType;
  customerContactGroup?: IContactGroup;
  locationRequestPayload?: Record<string, Record<string, unknown>>;
  contactRequestPayload?: Record<string, unknown>;
  isDefaultEditing?: boolean;
  hasCreateNewLocation?: boolean;
  hasCreateNewContact?: boolean;
  isAlternateVersionEnabled?: boolean;
  disableArNumberFilterToggle?: boolean;
  onChangeEmployees?: () => void;
}

const ADDRESS_TYPES_WITH_CONTACT_FIELDS = [ContactGroupAddressType.SHIP_TO];

export const UpdateAddressByType = <T,>({
  isOpen,
  parent,
  onClose,
  onSubmit,
  type,
  customerContactGroup,
  locationRequestPayload = {},
  contactRequestPayload = {},
  isDefaultEditing,
  hasCreateNewLocation,
  hasCreateNewContact,
  isAlternateVersionEnabled,
  disableArNumberFilterToggle,
  onChangeEmployees,
}: IProps<T>) => {
  const [pickedAccountsReceivableId, setPickedAccountsReceivableId] = useState<string>();
  const [isEmployeeWarningOpen, setIsEmployeeWarningOpen] = useState(false);

  const { clearNotification } = useNotificationContext();

  const hasContactFields = ADDRESS_TYPES_WITH_CONTACT_FIELDS.includes(type);
  const requiredAddressFields = useMemo(
    () => [
      ...DEFAULT_REQUIRED_FIELDS,
      AddressFields.LINE1,
      AddressFields.CITY,
      AddressFields.STATE,
    ],
    []
  );

  const defaultValues = useMemo(() => getDefaultValues(parent, type), [parent, type]);

  const handleOnSubmit =
    (reset: UseFormReset<IQuoteAddressForm>) => async (data: IQuoteAddressForm) => {
      const { success, updatedParent } = await onSubmit(
        mapUpdateContactGroupAddressForm(
          data,
          getAccountsReceivableId(pickedAccountsReceivableId, data.location),
          type
        )
      );

      if (!success) {
        return;
      }

      onSubmitSuccess();
      handleOnClose(reset, updatedParent);
    };

  const onSubmitSuccess = () => {
    if (!isAlternateVersionEnabled || type !== ContactGroupAddressType.BILL_TO) {
      return;
    }

    setIsEmployeeWarningOpen(true);
  };

  const onChangeLocation = (
    reset: UseFormReset<IQuoteAddressForm>,
    data: IQuoteAddressForm,
    option: IContactGroupAddress
  ) => {
    const country = option?.countryId
      ? {
          id: option.countryId,
          name: option.countryName,
          abbreviation: option.countryAbbreviation,
        }
      : DEFAULT_COUNTRY;

    setPickedAccountsReceivableId(
      option?.accountsReceivableId ||
        (type === ContactGroupAddressType.BILL_TO && isAlternateVersionEnabled
          ? DEFAULT_BILL_TO_ADDRESS_ACCOUNTS_RECEIVABLE_ID
          : '')
    );

    reset({
      ...data,
      address: {
        line1: option?.line1 || '',
        line2: option?.line2 || '',
        city: option?.city || '',
        zip: option?.zip || '',
        name: option?.name || '',
        country: country,
        state: option?.stateId
          ? {
              id: option.stateId,
              name: option.stateName,
            }
          : option?.state || '',
      },
      location: option,
    });
  };

  const onChangeContact = (
    reset: UseFormReset<IQuoteAddressForm>,
    data: IQuoteAddressForm,
    option: ICorporateUserAutocomplete
  ) => {
    reset({
      ...data,
      contact: option,
      contactName: option?.name || '',
      contactPhone: option?.phone || '',
      contactEmail: option?.email || '',
      contactCompanyName: option?.contactGroup?.name || '',
    });
  };

  const handleOnClose = (reset: UseFormReset<IQuoteAddressForm>, updatedParent = {}) => {
    clearNotification();
    setPickedAccountsReceivableId(undefined);
    const newParent = updatedParent ? { ...parent, ...updatedParent } : parent;

    reset(getDefaultValues(newParent, type));

    onClose(newParent);
  };

  return (
    <FormProvider
      schema={updateQuoteAddressSchema(hasContactFields, requiredAddressFields)}
      defaultValues={defaultValues}
      resetDefaultValues
    >
      <UpdateAddressByTypeFormFields
        isOpen={isOpen}
        onClose={handleOnClose}
        onSubmit={handleOnSubmit}
        type={type}
        onChangeLocation={onChangeLocation}
        onChangeContact={onChangeContact}
        customerContactGroup={customerContactGroup}
        locationRequestPayload={locationRequestPayload}
        contactRequestPayload={contactRequestPayload}
        hasContactFields={hasContactFields}
        isDefaultEditing={isDefaultEditing}
        hasCreateNewLocation={hasCreateNewLocation}
        hasCreateNewContact={hasCreateNewContact}
        pickedAccountsReceivableId={pickedAccountsReceivableId}
        isAlternateVersionEnabled={isAlternateVersionEnabled}
        requiredAddressFields={requiredAddressFields}
        disableArNumberFilterToggle={disableArNumberFilterToggle}
      />

      {isAlternateVersionEnabled && type === ContactGroupAddressType.BILL_TO && (
        <EmployeeWarning
          isOpen={isEmployeeWarningOpen}
          onClose={() => setIsEmployeeWarningOpen(false)}
          onChangeEmployees={onChangeEmployees}
        />
      )}
    </FormProvider>
  );
};
